import { FC } from 'react';
import { AgentFile } from '@/types/agents';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const STATUS_LABEL: Record<AgentFile['status'], string> = {
  UPLOADED: 'Na fila',
  PROCESSING: 'Processando',
  READY: 'Pronto',
  FAILED: 'Falhou',
};

const StatusIndicator: FC<{ file: AgentFile }> = ({ file }) => {
  if (file.status === 'READY') {
    return (
      <span
        className="inline-flex items-center gap-1 text-emerald-400 text-xs"
        data-testid="agent-file-status"
        data-status="READY"
      >
        <span className="material-symbols-outlined text-base">check_circle</span>
        {file.chunk_count} trechos
      </span>
    );
  }
  if (file.status === 'FAILED') {
    return (
      <span
        className="inline-flex items-center gap-1 text-red-400 text-xs"
        title={file.error_message || 'Falha ao processar'}
        data-testid="agent-file-status"
        data-status="FAILED"
      >
        <span className="material-symbols-outlined text-base">error</span>
        {STATUS_LABEL.FAILED}
      </span>
    );
  }
  // UPLOADED / PROCESSING → spinner
  return (
    <span
      className="inline-flex items-center gap-1 text-content-tertiary text-xs"
      data-testid="agent-file-status"
      data-status={file.status}
    >
      <span className="material-symbols-outlined text-base animate-spin">progress_activity</span>
      {STATUS_LABEL[file.status]}
    </span>
  );
};

interface Props {
  file: AgentFile;
  onDelete: () => void;
}

const AgentFileRow: FC<Props> = ({ file, onDelete }) => (
  <li
    data-testid="agent-file-row"
    className="flex items-center gap-3 rounded-lg bg-surface-secondary border border-surface-border px-3 py-2"
  >
    <span className="material-symbols-outlined text-content-tertiary">description</span>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-content-primary truncate">{file.filename}</p>
      <p className="text-xs text-content-tertiary">{formatSize(file.size_bytes)}</p>
    </div>
    <StatusIndicator file={file} />
    <button
      type="button"
      data-testid="file-delete"
      onClick={onDelete}
      aria-label={`Excluir ${file.filename}`}
      className="text-content-tertiary hover:text-red-400 transition"
    >
      <span className="material-symbols-outlined text-base">delete</span>
    </button>
  </li>
);

export default AgentFileRow;
